/**
 * Config de runtime.
 *
 * El frontend se construye UNA vez y se despliega en varios entornos. Si la URL
 * del API y las claves de Firebase quedaran horneadas en el bundle por
 * `import.meta.env`, cada entorno necesitaria su propio build. En su lugar el
 * contenedor escribe `/config.js` al arrancar (envsubst, ver
 * `docker-entrypoint.sh`) y la app lee `window.__APP_CONFIG__`.
 *
 * `import.meta.env` queda como fallback para `pnpm dev` y para los tests.
 */

export interface AppConfig {
  apiBaseUrl: string
  environment: 'development' | 'staging' | 'production' | 'test'
  firebase: {
    apiKey: string
    authDomain: string
    projectId: string
    appId: string
  }
}

export interface RuntimeConfigShape {
  API_BASE_URL?: string
  ENVIRONMENT?: string
  LOG_LEVEL?: string
  DEV_AUTH?: string
  DEV_USER?: string
  FIREBASE_API_KEY?: string
  FIREBASE_AUTH_DOMAIN?: string
  FIREBASE_PROJECT_ID?: string
  FIREBASE_APP_ID?: string
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfigShape
  }
}

function readRuntime(): RuntimeConfigShape {
  if (typeof window === 'undefined') return {}
  return window.__APP_CONFIG__ ?? {}
}

/**
 * envsubst deja el placeholder literal (`${API_BASE_URL}`) cuando la variable
 * no esta definida. Eso no es un valor: hay que tratarlo como ausente.
 */
function clean(value: string | undefined): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed || trimmed.startsWith('${')) return undefined
  return trimmed
}

function pick(runtimeValue: string | undefined, envValue: string | undefined): string {
  return clean(runtimeValue) ?? clean(envValue) ?? ''
}

let cached: AppConfig | null = null

export function getAppConfig(): AppConfig {
  if (cached) return cached

  const runtime = readRuntime()
  const env = import.meta.env

  const environment = pick(runtime.ENVIRONMENT, env.VITE_ENVIRONMENT) || 'development'

  cached = {
    apiBaseUrl: pick(runtime.API_BASE_URL, env.VITE_API_BASE_URL) || '/api/v1',
    environment: environment as AppConfig['environment'],
    firebase: {
      apiKey: pick(runtime.FIREBASE_API_KEY, env.VITE_FIREBASE_API_KEY),
      authDomain: pick(runtime.FIREBASE_AUTH_DOMAIN, env.VITE_FIREBASE_AUTH_DOMAIN),
      projectId: pick(runtime.FIREBASE_PROJECT_ID, env.VITE_FIREBASE_PROJECT_ID),
      appId: pick(runtime.FIREBASE_APP_ID, env.VITE_FIREBASE_APP_ID),
    },
  }

  return cached
}

/** Solo para tests: fuerza la relectura de la config. */
export function resetAppConfig(): void {
  cached = null
}

/**
 * Modo mock: MSW contesta el API y la sesion se da por iniciada.
 *
 * Existe para poder mirar la app funcionando mientras el backend se escribe en
 * paralelo (`VITE_USE_MSW=true pnpm dev`).
 *
 * `import.meta.env.DEV` va PRIMERO y no es decorativo: Vite lo reemplaza por
 * `false` al compilar, asi que en produccion la condicion se evalua en tiempo
 * de build, el import dinamico de MSW se elimina del bundle y no queda forma de
 * encender mocks por accidente en un entorno real.
 */
export function isMockMode(): boolean {
  return import.meta.env.DEV && clean(import.meta.env.VITE_USE_MSW as string | undefined) === 'true'
}

/* --------------------------------------------------------- modo dev-auth - */

/**
 * Identidad por header, contra el backend local.
 *
 * El servicio local corre con `AUTH_DEV_MODE=true` (ver `scripts/dev_local.py`)
 * y resuelve quién sos con `X-Dev-User: <email>` en vez de verificar un ID
 * token de Firebase. Eso permite dos cosas que con Firebase no se pueden hacer
 * en local: levantar la app sin consola de Firebase, y **cambiar de usuario sin
 * desloguearse**, que es lo único que hace mirable a ojo el aislamiento entre
 * empresas y entre roles.
 *
 * `import.meta.env.DEV` va PRIMERO y no es decorativo: Vite lo reemplaza por
 * `false` al compilar, así que en producción la condición se resuelve en tiempo
 * de build y no queda forma de mandar el header por accidente. El backend
 * además se niega a arrancar con `AUTH_DEV_MODE=true` y `ENVIRONMENT=production`.
 *
 *     VITE_DEV_AUTH=true VITE_API_BASE_URL=http://localhost:8000/api/v1 pnpm dev
 */
export function isDevAuthMode(): boolean {
  return (
    import.meta.env.DEV &&
    clean((import.meta.env.VITE_DEV_AUTH as string | undefined) ?? readRuntime().DEV_AUTH) ===
      'true'
  )
}

const DEV_USER_STORAGE_KEY = 'pm.dev-user'

function defaultDevUser(): string {
  return (
    clean((import.meta.env.VITE_DEV_USER as string | undefined) ?? readRuntime().DEV_USER) ??
    'cesar@leaflatam.com'
  )
}

let devUser: string | null = null

/** El email que viaja en `X-Dev-User`. Se recuerda entre recargas. */
export function getDevUser(): string {
  if (devUser) return devUser
  try {
    devUser = window.localStorage.getItem(DEV_USER_STORAGE_KEY) || defaultDevUser()
  } catch {
    devUser = defaultDevUser()
  }
  return devUser
}

export function setDevUser(email: string): void {
  devUser = email
  try {
    window.localStorage.setItem(DEV_USER_STORAGE_KEY, email)
  } catch {
    /* modo incógnito: el cambio vale para esta sesión y nada más */
  }
}

export function isFirebaseConfigured(): boolean {
  const { firebase } = getAppConfig()
  return Boolean(firebase.apiKey && firebase.authDomain && firebase.projectId && firebase.appId)
}
